'use client'

import Link from 'next/link'
import { MC, SERIF, SANS } from '@/lib/mycio/theme'
import { useMyCIOLanguage } from '@/lib/mycio/MyCIOLanguageContext'
import { getMyCIOYourDataPageCopy } from '@/lib/mycio/copy/yourDataPage'

/**
 * /mycio/your-data body — the data-trust deep dive, fully localized via getMyCIOYourDataPageCopy.
 * The thin server page.tsx owns <metadata> (static English, like the legal pages); every user-visible
 * string here comes from the copy module. Section ids (#nothing-to-connect …) stay static — only labels
 * localize. Every language translates the reviewed English (founder, R2: this page is UI, not legal text).
 */
const TOC_IDS = [
  'nothing-to-connect',
  'what-is-stored',
  'what-is-never-stored',
  'rough-is-fine',
  'delete',
  'sharing',
  'protected',
  'computed',
] as const

function H2({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2 id={id} style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 26, lineHeight: 1.25, color: MC.ink, margin: '52px 0 14px', scrollMarginTop: 80 }}>
      {children}
    </h2>
  )
}

function P({ children }: { children: React.ReactNode }) {
  return <p style={{ fontSize: 16, lineHeight: 1.7, color: MC.muted, margin: '0 0 14px' }}>{children}</p>
}

function Li({ children }: { children: React.ReactNode }) {
  return <li style={{ fontSize: 16, lineHeight: 1.7, color: MC.muted, margin: '0 0 8px' }}>{children}</li>
}

export default function YourDataBody() {
  const { language } = useMyCIOLanguage()
  const c = getMyCIOYourDataPageCopy(language)

  const tocLabels: Record<(typeof TOC_IDS)[number], string> = {
    'nothing-to-connect': c.toc.nothingToConnect,
    'what-is-stored': c.toc.whatIsStored,
    'what-is-never-stored': c.toc.whatIsNeverStored,
    'rough-is-fine': c.toc.roughIsFine,
    delete: c.toc.deleteEverything,
    sharing: c.toc.sharing,
    protected: c.toc.protectedHow,
    computed: c.toc.computed,
  }

  return (
    <div lang={language} style={{ fontFamily: SANS, background: MC.bg, minHeight: '100vh', color: MC.ink, WebkitFontSmoothing: 'antialiased' }}>
      <header
        style={{
          borderBottom: `1px solid ${MC.border}`,
          background: 'rgba(246,243,236,0.9)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          position: 'sticky',
          top: 0,
          zIndex: 20,
        }}
      >
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link href="/" style={{ fontFamily: SERIF, fontSize: 18, fontWeight: 500, color: MC.ink, textDecoration: 'none' }}>
            MyCIO<span style={{ color: MC.forest }}>.ai</span>
          </Link>
          <Link href="/" style={{ fontSize: 14, color: MC.muted, textDecoration: 'none' }}>{c.back}</Link>
        </div>
      </header>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '48px 24px 96px' }}>
        <p style={{ fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: MC.faint, fontWeight: 600, margin: '0 0 12px' }}>
          {c.eyebrow}
        </p>
        <h1 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 42, lineHeight: 1.1, margin: '0 0 16px', color: MC.ink }}>
          {c.titleBefore}<em style={{ fontStyle: 'italic', color: MC.forest }}>{c.titleEm}</em>{c.titleAfter}
        </h1>
        <P>{c.intro}</P>

        <nav aria-label={c.contentsLabel} style={{ background: MC.surface, border: `1px solid ${MC.border}`, borderRadius: 12, padding: '20px 24px', margin: '32px 0 16px' }}>
          <p style={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: MC.faint, fontWeight: 600, margin: '0 0 12px' }}>{c.contentsLabel}</p>
          <ol style={{ margin: 0, padding: '0 0 0 20px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {TOC_IDS.map((id) => (
              <li key={id} style={{ fontSize: 14.5, lineHeight: 1.4 }}>
                <a href={`#${id}`} style={{ color: MC.forest, textDecoration: 'none' }}>{tocLabels[id]}</a>
              </li>
            ))}
          </ol>
        </nav>

        <H2 id="nothing-to-connect">{c.toc.nothingToConnect}</H2>
        <P>{c.nothingToConnect.p1}</P>
        <P>{c.nothingToConnect.p2}</P>

        <H2 id="what-is-stored">{c.toc.whatIsStored}</H2>
        <P>{c.whatIsStored.lead}</P>
        <ul style={{ margin: '0 0 14px', padding: '0 0 0 20px' }}>
          {c.whatIsStored.items.map((item, i) => (
            <Li key={i}>{item}</Li>
          ))}
        </ul>
        <P>
          {c.whatIsStored.provenanceBefore}<strong style={{ color: MC.ink }}>{c.whatIsStored.provenanceStrong}</strong>{c.whatIsStored.provenanceAfter}{' '}
          <Link href="/privacy" style={{ color: MC.forest }}>{c.whatIsStored.privacyLinkLabel}</Link>{c.whatIsStored.provenanceEnd}
        </P>

        <H2 id="what-is-never-stored">{c.toc.whatIsNeverStored}</H2>
        <ul style={{ margin: '0 0 14px', padding: '0 0 0 20px' }}>
          {c.whatIsNeverStored.items.map((item, i) => (
            <Li key={i}>
              <strong style={{ color: MC.ink }}>{item.lead}</strong> {item.rest}
            </Li>
          ))}
        </ul>
        <P>{c.whatIsNeverStored.closing}</P>

        <H2 id="rough-is-fine">{c.toc.roughIsFine}</H2>
        <P>
          {c.roughIsFine.p1Before}<strong style={{ color: MC.ink }}>{c.roughIsFine.p1Strong}</strong>{c.roughIsFine.p1After}
        </P>
        <P>{c.roughIsFine.p2}</P>

        <H2 id="delete">{c.toc.deleteEverything}</H2>
        <P>{c.deleteSection.p1}</P>
        <P>{c.deleteSection.p2}</P>

        <H2 id="sharing">{c.sharingSection.heading}</H2>
        <P>
          {c.sharingSection.p1Before}<strong style={{ color: MC.ink }}>{c.sharingSection.p1Strong}</strong>{c.sharingSection.p1After}
        </P>
        <P>
          {c.sharingSection.p2Before}<strong style={{ color: MC.ink }}>{c.sharingSection.p2Strong}</strong>{c.sharingSection.p2After}
        </P>

        <H2 id="protected">{c.toc.protectedHow}</H2>
        <ul style={{ margin: '0 0 14px', padding: '0 0 0 20px' }}>
          {c.protectedSection.items.map((item, i) => (
            <Li key={i}>
              <strong style={{ color: MC.ink }}>{item.lead}</strong> {item.rest}
            </Li>
          ))}
        </ul>

        <H2 id="computed">{c.toc.computed}</H2>
        <P>{c.computedSection.p}</P>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginTop: 44, paddingTop: 32, borderTop: `1px solid ${MC.border}` }}>
          <Link
            href="/examples/retirement"
            style={{ display: 'inline-block', fontSize: 15.5, fontWeight: 600, color: MC.bg, background: MC.forest, padding: '14px 26px', borderRadius: 999, textDecoration: 'none' }}
          >
            {c.ctas.sample}
          </Link>
          <Link
            href="/sign-in#signup"
            style={{ display: 'inline-block', fontSize: 15.5, fontWeight: 600, color: MC.forest, background: MC.surface, border: `1px solid ${MC.forest}`, padding: '13px 24px', borderRadius: 999, textDecoration: 'none' }}
          >
            {c.ctas.start}
          </Link>
          <p style={{ fontSize: 13, color: MC.faint, margin: 0, flexBasis: '100%' }}>
            {c.ctas.note}
          </p>
        </div>
      </div>
    </div>
  )
}
